import { useState, useEffect, useCallback } from 'react';
import { Skill } from '../types';

interface CooldownState {
  [skillId: string]: {
    isOnCooldown: boolean;
    remainingTime: number;
    startTime: number;
    totalDuration: number;
  };
}

interface GlobalCooldown {
  isActive: boolean;
  remainingTime: number;
  startTime: number;
  totalDuration: number;
}

interface ChargeState {
  [groupId: string]: {
    currentCharges: number;
    maxCharges: number;
    chargeTime: number;
    nextChargeReadyAt: number;  // 下一个积蓄恢复的时间戳
    consumeInterval: number;    // 连续释放间隔时间（秒）
    emptyBehavior: 'recharge' | 'block'; // 积蓄为空时的行为
    lastConsumeTime: number;    // 上次消耗积蓄的时间戳
    skills: string[];           // 共享此积蓄组的技能ID列表
  };
}

interface SharedCooldownGroup {
  [groupId: string]: {
    isOnCooldown: boolean;
    remainingTime: number;
    startTime: number;
    totalDuration: number;
  };
}

export const useSkillCooldowns = (jobGcd: number = 2.5, activeEffects: any = {}) => {
  const [cooldowns, setCooldowns] = useState<CooldownState>({});
  const [globalCooldown, setGlobalCooldown] = useState<GlobalCooldown>({
    isActive: false,
    remainingTime: 0,
    startTime: 0,
    totalDuration: 0
  });
  const [charges, setCharges] = useState<ChargeState>({});
  const [sharedCooldowns, setSharedCooldowns] = useState<SharedCooldownGroup>({});

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      
      // 更新技能冷却
      setCooldowns(prev => {
        const updated = { ...prev };
        let hasChanges = false;

        Object.keys(updated).forEach(skillId => {
          const cooldown = updated[skillId];
          if (cooldown.isOnCooldown) {
            const elapsed = (now - cooldown.startTime) / 1000;
            const newRemainingTime = Math.max(0, cooldown.totalDuration - elapsed);
            
            if (newRemainingTime !== cooldown.remainingTime) {
              updated[skillId] = {
                ...cooldown,
                remainingTime: newRemainingTime,
                isOnCooldown: newRemainingTime > 0
              };
              hasChanges = true;
            }
          }
        });

        return hasChanges ? updated : prev;
      });

      // 更新全局冷却
      setGlobalCooldown(prev => {
        if (prev.isActive) {
          const elapsed = (now - prev.startTime) / 1000;
          const newRemainingTime = Math.max(0, prev.totalDuration - elapsed);
          
          if (newRemainingTime !== prev.remainingTime) {
            return {
              ...prev,
              remainingTime: newRemainingTime,
              isActive: newRemainingTime > 0
            };
          }
        }
        return prev;
      });

      // 更新积蓄系统
      setCharges(prev => {
        const updated = { ...prev };
        let hasChanges = false;

        Object.keys(updated).forEach(groupId => {
          const charge = updated[groupId];
          if (charge.currentCharges < charge.maxCharges && charge.nextChargeReadyAt > 0) {
            if (now >= charge.nextChargeReadyAt) {
              const newCharges = Math.min(charge.maxCharges, charge.currentCharges + 1);
              const nextReadyAt = newCharges < charge.maxCharges ? 
                now + (charge.chargeTime * 1000) : 0;
              
              updated[groupId] = {
                ...charge,
                currentCharges: newCharges,
                nextChargeReadyAt: nextReadyAt
              };
              hasChanges = true;
            }
          }
        });

        return hasChanges ? updated : prev;
      });

      // 更新共享冷却时间组
      setSharedCooldowns(prev => {
        const updated = { ...prev };
        let hasChanges = false;

        Object.keys(updated).forEach(groupId => {
          const cooldown = updated[groupId];
          if (cooldown.isOnCooldown) {
            const elapsed = (now - cooldown.startTime) / 1000;
            const newRemainingTime = Math.max(0, cooldown.totalDuration - elapsed);
            
            if (newRemainingTime !== cooldown.remainingTime) {
              updated[groupId] = {
                ...cooldown,
                remainingTime: newRemainingTime,
                isOnCooldown: newRemainingTime > 0
              };
              hasChanges = true;
            }
          }
        });

        return hasChanges ? updated : prev;
      });
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const initializeCharges = useCallback((skill: Skill) => {
    if (skill.charges) {
      const groupId = skill.sharedCooldownGroup || skill.id;
      setCharges(prev => {
        if (!prev[groupId]) {
          return {
            ...prev,
            [groupId]: {
              currentCharges: skill.charges!.initialCharges || skill.charges!.maxCharges,
              maxCharges: skill.charges!.maxCharges,
              chargeTime: skill.charges!.chargeTime,
              nextChargeReadyAt: 0,
              consumeInterval: skill.charges!.consumeInterval || 1.5,
              emptyBehavior: skill.charges!.emptyBehavior || 'recharge',
              lastConsumeTime: 0,
              skills: [skill.id]
            }
          };
        } else {
          // 如果组已存在，添加技能ID到列表中
          const existingGroup = prev[groupId];
          if (!existingGroup.skills.includes(skill.id)) {
            return {
              ...prev,
              [groupId]: {
                ...existingGroup,
                skills: [...existingGroup.skills, skill.id]
              }
            };
          }
        }
        return prev;
      });
    }
  }, []);

  // 检查充能减少条件
  const checkChargeReduction = useCallback((skill: Skill): number => {
    if (!skill.charges?.chargeReduction) return 0;
    
    const { conditions, costType, costResource, reductionValue } = skill.charges.chargeReduction;
    
    // 检查所有条件是否满足
    const conditionsMet = conditions.every(condition => {
      switch (condition.type) {
        case 'status':
          const statusId = condition.statusId || condition.condition;
          return activeEffects[statusId] && activeEffects[statusId].remainingTime > 0;
        default:
          return false;
      }
    });
    
    if (!conditionsMet) return 0;
    
    // 检查是否有足够的资源
    if (costType === 'status') {
      const effect = activeEffects[costResource];
      if (!effect || effect.remainingTime <= 0) return 0;
      // 对于诗心这样的状态效果，不检查层数，只要存在即可
    }
    
    return reductionValue;
  }, [activeEffects]);

  // 消耗充能减少资源
  const consumeChargeReductionResource = useCallback((skill: Skill, onConsumeEffect?: (effectId: string, value: number) => void) => {
    if (!skill.charges?.chargeReduction) return;
    
    const { costType, costResource, costValue, autoConsume } = skill.charges.chargeReduction;
    
    if (autoConsume && costType === 'status' && onConsumeEffect) {
      onConsumeEffect(costResource, costValue);
    }
  }, []);

  const startCooldown = useCallback((skill: Skill, onConsumeEffect?: (effectId: string, value: number) => void) => {
    const now = Date.now();
    
    // 初始化积蓄系统（如果需要）
    initializeCharges(skill);
    
    // 处理积蓄系统技能
    if (skill.charges) {
      const groupId = skill.sharedCooldownGroup || skill.id;
      const reductionTime = checkChargeReduction(skill);
      
      setCharges(prev => {
        const currentCharge = prev[groupId];
        if (currentCharge && currentCharge.currentCharges > 0) {
          const newCharges = currentCharge.currentCharges - 1;
          
          // 计算下一个充能时间，考虑减少效果
          let nextReadyAt = currentCharge.nextChargeReadyAt;
          if (nextReadyAt === 0) {
            const baseChargeTime = currentCharge.chargeTime;
            const actualChargeTime = Math.max(0, baseChargeTime - reductionTime);
            nextReadyAt = now + (actualChargeTime * 1000);
          } else if (reductionTime > 0) {
            // 如果有减少效果，提前充能完成时间
            nextReadyAt = Math.max(now, nextReadyAt - (reductionTime * 1000));
          }
          
          // 消耗资源
          if (reductionTime > 0) {
            consumeChargeReductionResource(skill, onConsumeEffect);
          }
          
          return {
            ...prev,
            [groupId]: {
              ...currentCharge,
              currentCharges: newCharges,
              nextChargeReadyAt: nextReadyAt,
              lastConsumeTime: now
            }
          };
        }
        return prev;
      });
    }
    
    // 只有GCD技能才启动全局冷却
    if (skill.gcd === true) {
      setGlobalCooldown({
        isActive: true,
        remainingTime: jobGcd,
        startTime: now,
        totalDuration: jobGcd
      });
    }
    
    // 处理共享冷却时间组
    if (skill.sharedCooldownGroup && skill.recastTime && skill.recastTime > 0) {
      setSharedCooldowns(prev => ({
        ...prev,
        [skill.sharedCooldownGroup!]: {
          isOnCooldown: true,
          remainingTime: skill.recastTime!,
          startTime: now,
          totalDuration: skill.recastTime!
        }
      }));
    } else if (skill.recastTime && skill.recastTime > 0 && !skill.charges) {
      // 技能自身的冷却时间（非积蓄技能且非共享冷却）
      setCooldowns(prev => ({
        ...prev,
        [skill.id]: {
          isOnCooldown: true,
          remainingTime: skill.recastTime!,
          startTime: now,
          totalDuration: skill.recastTime!
        }
      }));
    }
  }, [jobGcd, initializeCharges, checkChargeReduction, consumeChargeReductionResource]);

  const isSkillOnCooldown = (skill: Skill): boolean => {
    const now = Date.now();
    
    // 初始化积蓄系统（如果需要）
    initializeCharges(skill);
    
    // 积蓄技能检查
    if (skill.charges) {
      const groupId = skill.sharedCooldownGroup || skill.id;
      const chargeState = charges[groupId];
      
      if (!chargeState) return true;
      
      // 没有积蓄层数时，根据emptyBehavior决定
      if (chargeState.currentCharges <= 0) {
        return true; // 积蓄为0时技能不可用，需要等待积蓄恢复
      }
      
      // 有积蓄时，检查连续释放间隔
      if (chargeState.lastConsumeTime > 0) {
        const timeSinceLastConsume = (now - chargeState.lastConsumeTime) / 1000;
        if (timeSinceLastConsume < chargeState.consumeInterval) {
          return true; // 还在释放间隔内，不可用
        }
      }
      
      // 有积蓄且不在间隔内，技能可用
      return false;
    }
    
    // 只有GCD技能需要检查全局冷却
    if (skill.gcd === true && globalCooldown.isActive) {
      return true;
    }
    
    // 检查共享冷却时间组
    if (skill.sharedCooldownGroup) {
      const sharedCooldown = sharedCooldowns[skill.sharedCooldownGroup];
      if (sharedCooldown?.isOnCooldown) {
        return true;
      }
    }
    
    // 检查技能自身的冷却
    return cooldowns[skill.id]?.isOnCooldown || false;
  };

  const getCooldownRemaining = (skill: Skill): number => {
    const now = Date.now();
    
    // 积蓄技能的冷却时间显示
    if (skill.charges) {
      const groupId = skill.sharedCooldownGroup || skill.id;
      const chargeState = charges[groupId];
      
      if (chargeState) {
        // 如果有积蓄但在连续释放间隔内，显示间隔剩余时间
        if (chargeState.currentCharges > 0 && chargeState.lastConsumeTime > 0) {
          const timeSinceLastConsume = (now - chargeState.lastConsumeTime) / 1000;
          const remainingInterval = chargeState.consumeInterval - timeSinceLastConsume;
          if (remainingInterval > 0) {
            return remainingInterval;
          }
        }
        
        // 如果没有积蓄，显示下个积蓄恢复时间
        if (chargeState.currentCharges <= 0 && chargeState.nextChargeReadyAt > 0) {
          return Math.max(0, (chargeState.nextChargeReadyAt - now) / 1000);
        }
        
        // 如果有积蓄且不在间隔内，但还有积蓄在恢复中，显示恢复时间
        if (chargeState.currentCharges < chargeState.maxCharges && chargeState.nextChargeReadyAt > 0) {
          return Math.max(0, (chargeState.nextChargeReadyAt - now) / 1000);
        }
      }
      return 0;
    }
    
    // 检查共享冷却时间组
    if (skill.sharedCooldownGroup) {
      const sharedCooldown = sharedCooldowns[skill.sharedCooldownGroup];
      if (sharedCooldown?.isOnCooldown) {
        return sharedCooldown.remainingTime;
      }
    }
    
    // 检查技能自身的冷却时间
    const skillCooldown = cooldowns[skill.id]?.remainingTime || 0;
    
    // 只有GCD技能才检查全局冷却
    if (skill.gcd === true && globalCooldown.isActive) {
      // 返回两者中较大的值
      return Math.max(globalCooldown.remainingTime, skillCooldown);
    }
    
    // 非GCD技能或全局冷却不活跃时，返回技能自身冷却
    return skillCooldown;
  };

  const getChargeInfo = (skill: Skill) => {
    if (!skill.charges) return null;
    
    const groupId = skill.sharedCooldownGroup || skill.id;
    const chargeState = charges[groupId];
    if (!chargeState) return null;
    
    return {
      current: chargeState.currentCharges,
      max: chargeState.maxCharges,
      nextChargeIn: chargeState.nextChargeReadyAt > 0 ? 
        Math.max(0, (chargeState.nextChargeReadyAt - Date.now()) / 1000) : 0
    };
  };

  const resetAllCooldowns = useCallback(() => {
    setCooldowns({});
    setGlobalCooldown({
      isActive: false,
      remainingTime: 0,
      startTime: 0,
      totalDuration: 0
    });
    setCharges({});
    setSharedCooldowns({});
  }, []);

  return {
    startCooldown,
    isSkillOnCooldown,
    getCooldownRemaining,
    getChargeInfo,
    globalCooldown,
    resetAllCooldowns,
    initializeCharges
  };
};